import { Title } from "components";
import { endOfDay } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "redux/store";
import { getTradingTotalsReport, getTradingTotalsReportExport } from "services/reportservices";
import { Row, Col, Card, Button, OverlayTrigger, Tooltip, Spinner, Tabs, Tab } from "react-bootstrap";
import { ZMultiSelectDropdown } from "components/ZMultiSelectDropdown";
import { ZDateTimePicker } from "components/ZDateTimePicker";
import { TimedMessage } from "components/TimedMessage";
import { Link } from "react-router-dom";
import { cleanTradingTotals, updateReportData, updateReportView } from "redux/slices/tradingTotalsReportPageSlice";
import { headers } from "TableConfigs/TradingTotalsReportConfig";
import { StoreExtended } from "models/dbModels/stores/Store";
import { TradingTotalPeriodData } from "models/functionModels/reportFunctions/TradingTotalsReportFunction";
import { ZLabel } from "components/ZLabel";
import { ZNumberInput } from "components/ZNumberInput";
import { AgGridReact } from "ag-grid-react";
import PriceCellRenderer from "components/AgGrid/CellRenderers/PriceCellRenderer";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import PercentCellRenderer from "components/AgGrid/CellRenderers/PercentCellRenderer";
import { ColDef } from "ag-grid-enterprise";
import {} from "helpers/dateFormater";

export function TradingTotalsReport() {
	const pageState = useAppSelector((state: RootState) => state.pageTradingTotalsReport);
	const stores = useAppSelector((state: RootState) => state.stores);
	const dispatch = useAppDispatch();
	const [generating, setGenerating] = useState(false);
	const [constructing, setConstructing] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [startDate, setStartDate] = useState<Date | null>(getDateNow());
	const [startDateError, setStartDateError] = useState<string | null>();
	const [endDate, setEndDate] = useState<Date | null>(getDateNow());
	const [endDateError, setEndDateError] = useState<string | null>();
	const [selectedStores, setSelectedStores] = useState<StoreExtended[]>([]);
	const [generatedStores, setGeneratedStores] = useState<StoreExtended[]>([]);
	const [generateButtonEnabled, setGenerateButtonEnabled] = useState<boolean>(false);
	const [generateButtonTooltip, setGenerateButtonTooltip] = useState<string>("");
	const [exportButtonEnabled, setExportButtonEnabled] = useState<boolean>(false);
	const [exportButtonTooltip, setExportButtonTooltip] = useState<string>("");
	const [reportType, setReportType] = useState<number>(3);
	const [costFilterType, setCostFilterType] = useState<number>(1);
	const [reportColumns, setReportColumns] = useState<ColDef<any, any>[]>([]);	
	const typeList = [
		{ id: 3, name: "PBS Base" }, 
		{ id: 1, name: "Last Invoice" },
		{ id: 2, name: "Average Cost" }];
	const costFilterList = [
		{ id: 1, name: ">=" },
		{ id: 2, name: "<=" },
		{ id: 3, name: "=" },
	];
	const [costFilterValue, setCostFilterValue] = useState<number|null|undefined>(0);
	const [excludeNursingHome, setExcludeNursingHome] = useState<boolean>(false);

	useEffect(() => {
		dispatch(cleanTradingTotals());
		setReportType(3);
		setReportColumns(headers.filter((x: ColDef<any, any>)=> x.field != "basePrice" || x.headerName == "PBS/WS1"));
	}, []);

	useEffect(() => {
		dispatch(cleanTradingTotals());
		switch(reportType){
			case 2:
				setReportColumns(headers.filter((x: ColDef<any, any>)=> x.field != "basePrice" || x.headerName == "Average Cost"));
				break;
			case 1:
				setReportColumns(headers.filter((x: ColDef<any, any>)=> x.field != "basePrice" || x.headerName == "Last Inv"));
				break;
			default:
				setReportColumns(headers.filter((x: ColDef<any, any>)=> x.field != "basePrice" || x.headerName == "PBS/WS1"));
				break;
		}

	}, [reportType]);

	function getDateNow() {
		const now = new Date();
		now.setHours(0);
		now.setMinutes(0);
		now.setSeconds(0);
		now.setMilliseconds(0);

		return now;
	}

	function FilterReportByStoreId(key: string | null) {
		if (key === "all") {
			CombineTradingTotalData(pageState.reportData);
		} else {
			let filteredData = pageState.reportData.filter(rp => rp.storeId.toString() === key);
			dispatch(updateReportView(filteredData));
		}
	}



	function CombineTradingTotalData(tradingData: TradingTotalPeriodData[]) {
		let combinedRows: TradingTotalPeriodData[] = [];
		tradingData.forEach(rep => {
			if (combinedRows.filter(ty => ty.type === rep.type).length === 0) {
				let newdata: TradingTotalPeriodData = {
					type: rep.type,
					storeId: 0,
					rangeStartDate: rep.rangeStartDate,
					rangeEndDate: rep.rangeEndDate,
					tradingTotalPeriodKey: rep.tradingTotalPeriodKey,
					tradingTotalPeriodDescription: rep.tradingTotalPeriodDescription,
					basePrice: rep.basePrice,
					eScriptIncentive: rep.eScriptIncentive,
					pbsDiscount: rep.pbsDiscount,
					premFreeIncentive: rep.premFreeIncentive,
					premFeeIncentive: rep.premFeeIncentive,
					surcharges: rep.surcharges,
					fees: rep.fees,
					markup: rep.markup,
					grossProfit: rep.grossProfit,
					salesValue: rep.salesValue,
					patientGST: rep.patientGST,
					govRecoveryCost: rep.govRecoveryCost,
					patientCost: rep.patientCost,
					order: rep.order,
					scriptCount: rep.scriptCount,
					gpBasePercentage: rep.gpBasePercentage,
					gpSalesPercentage: rep.gpSalesPercentage,
					feeSalesPercentage: rep.feeSalesPercentage,
					feeGPPercentage: rep.feeGPPercentage
				};

				combinedRows.push(newdata);
			} else {
				combinedRows.forEach(row => {
				
					if (row.type == rep.type) {
						row.basePrice = row.basePrice == null ? rep.basePrice : row.basePrice + rep.basePrice;
						row.eScriptIncentive = row.eScriptIncentive == null ? rep.eScriptIncentive : row.eScriptIncentive + rep.eScriptIncentive;
						row.pbsDiscount = row.pbsDiscount == null ? rep.pbsDiscount : row.pbsDiscount + rep.pbsDiscount;
						row.premFreeIncentive = row.premFreeIncentive == null ? rep.premFreeIncentive : row.premFreeIncentive + rep.premFreeIncentive;
						row.premFeeIncentive = row.premFeeIncentive == null ? rep.premFeeIncentive : row.premFeeIncentive + rep.premFeeIncentive;
						row.surcharges = row.surcharges == null ? rep.surcharges : row.surcharges + rep.surcharges;
						row.fees = row.fees == null ? rep.fees : row.fees + rep.fees;
						row.markup = row.markup == null ? rep.markup : row.markup + rep.markup;
						row.grossProfit = row.grossProfit == null ? rep.grossProfit : row.grossProfit + rep.grossProfit;
						row.salesValue = row.salesValue == null ? rep.salesValue : row.salesValue + rep.salesValue;
						row.patientGST = row.patientGST == null ? rep.patientGST : row.patientGST + rep.patientGST;

						row.govRecoveryCost = row.govRecoveryCost == null ? rep.govRecoveryCost : row.govRecoveryCost + rep.govRecoveryCost;
						row.patientCost = row.patientCost == null ? rep.patientCost : row.patientCost + rep.patientCost;
						row.order = row.order == null ? rep.order : row.order + rep.order;
						row.scriptCount = row.scriptCount == null ? rep.scriptCount : row.scriptCount + rep.scriptCount;
						row.pbsDiscount = row.pbsDiscount == null ? rep.pbsDiscount : row.pbsDiscount + rep.pbsDiscount;

						if (row.basePrice != 0){
							row.gpBasePercentage = Math.round((row.grossProfit / row.basePrice) * 100);
						}
						if (row.grossProfit != 0){
							row.gpSalesPercentage = Math.round((row.fees / row.grossProfit) * 100);
						}
						if (row.salesValue != 0){
							row.feeSalesPercentage = Math.round((row.fees / row.salesValue) * 100);
						}
						if (row.grossProfit != 0){							
						    row.feeGPPercentage = Math.round((row.fees / row.grossProfit) * 100);							
						}
					}
				});
			}
		});
		dispatch(updateReportView(combinedRows));
	}

	const showTable = useMemo(() => {
	return <>
		<div style={{height: 500}}>
				<AgGridReact

					className={`ag-theme-quartz ag-theme-custom w-100`}
					rowData={pageState.reportView}
					columnDefs={reportColumns}
					pagination={false}
					components={{
						baseCellRenderer: BaseCellRenderer,
						priceCellRenderer: PriceCellRenderer,
						percentCellRenderer: PercentCellRenderer,
					}}
					rowClassRules={ {
						"ag-grid-highlight-row": params => params.api.getValue("type", params.node) == "Totals" ||
						params.api.getValue("type", params.node) == "GovtSubt."								
					}}
				/>
			</div>
		</>	
	},[reportColumns, pageState.reportView]);

	useEffect(() => {
		if (!selectedStores || selectedStores.length === 0) {
			setGenerateButtonEnabled(false);
			setExportButtonEnabled(false);
			setGenerateButtonTooltip("Please select at least one store to generate report for");
			setExportButtonTooltip("Please select at least one store to export report for");
		} else if (startDateError || endDateError) {
			setGenerateButtonEnabled(false);
			setExportButtonEnabled(false);
			setGenerateButtonTooltip("Please select a valid start and end date to generate report for");
			setExportButtonTooltip("Please select a valid start and end date to export report for");
		} else {
			setGenerateButtonEnabled(true);
			setExportButtonEnabled(true);
			setGenerateButtonTooltip("");
			setExportButtonTooltip("");
		}
	}, [selectedStores, startDateError, endDateError]);

	const GenerateButton =
		<div>
			<Button className="ms-1 cta-button"
				disabled={generating}
				onClick={() => {
					setGeneratedStores(selectedStores);
					setGenerating(true);
					setConstructing(true);
					const endDateTime  = endDate ? new Date(new Date(new Date(endDate.setHours(23)).setMinutes(59)).setSeconds(59)): null;
					getTradingTotalsReport({
						startDateTime: startDate ? startDate.GetLocalDateNoTimezone()  : null,
						endDateTime: endDate ? endOfDay(endDate).GetLocalDateNoTimezone(): null,
						storeIds: selectedStores?.map(st => st.id) ?? [],
						costType: reportType,
						filterType: costFilterType,
						filterCost: costFilterValue == undefined ? 0: costFilterValue,
						excludeNursingHome: excludeNursingHome
					}).then(data => {
						if (data == null) {
							return;
						}
						let combinedData: TradingTotalPeriodData[] = [];
						data.stores.forEach((store: any) => {
							if(store.response.data == null){
								return;
							}
							store.response.data.tradingTotalReportData.forEach((tradingData: any) => {
								tradingData.storeId = store.storeId
								combinedData.push(tradingData);
							});
						});

						dispatch(updateReportData(combinedData));
						CombineTradingTotalData(combinedData);				

					}).finally(() => {
						setGenerating(false);
					});
				}}>
				{generating ? <Spinner className="spinner-border-sm me-1" color="white" /> : <></>}
				{generating ? <>Generating...</> : <>Generate</>}
			</Button>
		</div>;

	const GenerateButtonWithTooltip =
		<OverlayTrigger placement="top" overlay={
			<Tooltip id="tooltip-bottom" style={{ position: "fixed" }}>
				{generateButtonTooltip}
			</Tooltip>}
		>
			{GenerateButton}
		</OverlayTrigger>;

	// onClick={exportTillReportClick} >
	const ExportButton =
		<div><Button className="ms-1 cta-button" variant="light" disabled={ false }
			onClick={() => {
				
				getTradingTotalsReportExport({
					startDateTime: startDate ? startDate.GetLocalDateNoTimezone()  : null,
					endDateTime: endDate? endOfDay(endDate).GetLocalDateNoTimezone(): null,
					storeIds: selectedStores?.map(st => st.id) ?? [],
					costType: reportType,
					filterType: costFilterType,
					filterCost: costFilterValue == undefined? 0: costFilterValue,
					excludeNursingHome: excludeNursingHome
				});
			}} >
			{exporting ? <><Spinner className="spinner-border-sm me-1" color="white" /></> : <></>}
			{exporting ? <>Exporting...</> : <>Export</>}
		</Button></div>;

	const ExportButtonWithTooltip =
		<OverlayTrigger placement="top" overlay={
			<Tooltip id="tooltip-bottom" style={{ position: "fixed" }}>
				{exportButtonTooltip}
			</Tooltip>}
		>
			{ExportButton}
		</OverlayTrigger>;

	const getTabs = (stores: StoreExtended[]) => {
		console.log("Getting tabs for: ", stores);
		return <Tabs
			className="ag-grid-tabs-container"
			defaultActiveKey="all"
			id="trading-totals-tabs"
			onSelect={FilterReportByStoreId}
		>
			{				
				<Tab title={stores.length === 1? stores[0].name: "Combined Data"} eventKey="all" key="all" />				
			}
			{		
			
				(stores.length > 1) &&
				stores.map((ss) => {
					if(ss.name != "Combined Data")		
						return <Tab title={ss.name} eventKey={ss.id} key={ss.id}></Tab>
				})
			}
		</Tabs>
	};

	return (
		<>
			<Title pageTitle={"Trading Totals Report"} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body className="mb-1">
							<div className={`${exporting || generating ? "disabled-div" : ""}`}>
								<Row className="d-flex align-items-end mb-1">
									<Col xs={6} lg={4} xxl={2}>
										<ZMultiSelectDropdown
											hasSearch={true}
											canClear={true}
											label="Stores:"
											values={selectedStores}
											placeholder="Select stores"
											hasAllOption={true}
											options={stores.table}
											display={(s) => s.name}
											value={(s) => s.id}
											minMenuWidthPx={300}
											disabled={(s) => !s.connected}
											onSelectionChange={(e) => setSelectedStores(e)}
										/>
									</Col>
									<Col xs={6} lg={2} xxl={2}>
										<ZSingleSelectDropdown
											label="Use Cost:"
											options={typeList}
											display={(s) => s.name}
											optionKey={(s) => s.id}
											value={typeList.find((i: any)=> i.id == reportType)}
											onSelectionChange={(e) => setReportType(e!.id)}
										/>
									</Col>
									<Col xs={6} lg={4} xxl={2} className="d-flex flex-column">
										<ZLabel preventSelect>Cost Filter:</ZLabel>
										<div className="w-100 d-inline-flex gap-1">
											<ZSingleSelectDropdown
												value={costFilterList.find((i: any)=> i.id == costFilterType)}
												options={costFilterList}
												onSelectionChange={(e) => setCostFilterType(e!.id)}
												display={(s) => s.name}
												optionKey={(s) => s.id}
												containerStyle={{ flex: 2, maxWidth: 60 }}
											/>
											<ZNumberInput
												placeholder="Enter dollars only"
												label=" "
												value={costFilterValue}
												onChange={(e: number | null | undefined) => setCostFilterValue(e)}
												type="integer-price"
												containerStyle={{ flex: 4 }}
											/>
										</div>
									</Col>
									<Col xs={6} lg={2} xxl={2}>
										<ZSingleSelectDropdown
											label="Facility Scripts:"
											options={[
												{ id: 1, name: "Include" },
												{ id: 2, name: "Exclude" }
											]}
											display={(s) => s.name}
											optionKey={(s) => s.id}
											value={excludeNursingHome ? { id: 2, name: "Exclude" } : { id: 1, name: "Include" }}
											onSelectionChange={(e) => setExcludeNursingHome(e.id == 2)}
										/>
									</Col>
									<Col xs={6} lg={3} xxl={2} className="ms-auto">
										<ZDateTimePicker
											canClear={false}
											value={startDate}
											label="Start Date"
											error={startDateError}
											onError={(e) => setStartDateError(e)}
											onChanged={(d) => setStartDate(d)}
											hasTime={false}
										/>
									</Col>
									<Col xs={6} lg={3} xxl={2}>
										<ZDateTimePicker
											canClear={false}
											value={endDate}
											label="End Date"
											error={endDateError}
											onError={(e) => setEndDateError(e)}
											onChanged={(d) => setEndDate(d)}
											hasTime={false}
										/>
									</Col>
								</Row>
							</div>
							<Row>
								<Col xs={12}>
									{getTabs(generatedStores)}
								</Col>
							</Row>
							<Row>
								{showTable}
							</Row>
							<Row className='mt-3'>
								<Col xs={3}>
									<Link to="/app/reports" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
										<i className="mdi mdi-arrow-left"></i> Back to Reports {' '}
									</Link>
								</Col>
								<Col xs={9} className="d-flex flex-reverse-row justify-content-end">
									<TimedMessage message="Please wait while we generate your report..." delay={30} enabled={generating} className="mb-0 py-1 px-2 ms-auto" />
									<TimedMessage message="Please wait while we export your report..." delay={30} enabled={exporting} className="mb-0 py-1 px-2 ms-auto" />
									{ ExportButton }
									{generateButtonEnabled ? GenerateButton : GenerateButtonWithTooltip}
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}
